import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Mail, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import FilterBar from './FilterBar';
import LogTable from './LogTable';
import RequestTimeline from './RequestTimeline';
import { useLogs, BOSH_FILTRELER } from '../hooks/useLogs';
import { MUSTERILER } from '../constants/filters';

// Müşteri iletişim bilgileri — gerçek verilerle doldur
const MUSTERI_ILETISIM = {
  // '101': { tel: '+90 212 000 0000', email: 'it@musteri.com' },
};

export default function CustomerDetail() {
  const { musteri } = useParams();
  const navigate    = useNavigate();
  const musteriId   = decodeURIComponent(musteri);
  const musteriAdi  = MUSTERILER.find(m => m.value === musteriId)?.label ?? musteriId;
  const iletisim    = MUSTERI_ILETISIM[musteriId] ?? {};

  const [filtreler, setFiltreler] = useState({
    ...BOSH_FILTRELER,
    musteri: musteriId,
  });
  const [secilenGroup, setSecilenGroup] = useState(null);

  const {
    data, total, loading, error, lastFetch, refresh,
    sayfa, setSayfa, toplamSayfa, sayfaBoyutu,
  } = useLogs(filtreler);

  function handleFiltreDegisimi(yeni) {
    setFiltreler({ ...yeni, musteri: musteriId });
  }

  function handleRowClick(group) {
    if (group.requestId) {
      setSecilenGroup(group);
    }
  }

  return (
    <div>
      {/* Başlık */}
      <div className='flex items-center justify-between mb-4'>
        <div className='flex items-center gap-3'>
          <button onClick={() => navigate('/')}
            className='p-2 rounded-lg hover:bg-gray-100 transition-colors'>
            <ArrowLeft size={18} className='text-slate-500' />
          </button>
          <div>
            <h1 className='text-xl font-bold text-slate-800'>{musteriAdi}</h1>
            <p className='text-xs text-slate-400'>ID: {musteriId}</p>
            {lastFetch && (
              <p className='text-xs text-slate-400'>
                Son güncelleme: {lastFetch.toLocaleTimeString('tr-TR')}
              </p>
            )}
          </div>
        </div>

        <div className='flex items-center gap-3'>
          {iletisim.tel && (
            <a href={`tel:${iletisim.tel}`}
              className='flex items-center gap-2 px-4 py-2 bg-green-600
                         hover:bg-green-700 text-white rounded-lg text-sm
                         font-medium transition-colors'>
              <Phone size={14} /> Müşteriyi Ara
            </a>
          )}
          {iletisim.email && (
            <a href={`mailto:${iletisim.email}?subject=Cloud4Feed Teknik Bilgilendirme`}
              className='flex items-center gap-2 px-4 py-2 bg-blue-600
                         hover:bg-blue-700 text-white rounded-lg text-sm
                         font-medium transition-colors'>
              <Mail size={14} /> Mail Gönder
            </a>
          )}
          <button onClick={refresh}
            className='p-2 border border-gray-200 rounded-lg hover:bg-gray-50
                       transition-colors'>
            <RefreshCw size={14}
              className={loading ? 'animate-spin text-blue-500' : 'text-gray-400'} />
          </button>
        </div>
      </div>

      {/* Filtre çubuğu — müşteri sabit */}
      <FilterBar
        filtreler={filtreler}
        onChange={handleFiltreDegisimi}
        musteriSecimGoster={false}
      />

      {/* Tablo */}
      {error ? (
        <div className='bg-red-50 border border-red-200 rounded-xl p-4
                        text-sm text-red-600'>
          {error}
        </div>
      ) : loading ? (
        <div className='text-center py-16 text-slate-400 text-sm'>
          Yükleniyor...
        </div>
      ) : (
        <>
          <LogTable
            data={data}
            total={total}
            onRowClick={handleRowClick}
          />

          {/* Sayfalama */}
          {toplamSayfa > 1 && (
            <div className='flex items-center justify-between mt-4 px-1'>
              <p className='text-xs text-slate-400'>
                {(sayfa - 1) * sayfaBoyutu + 1}–{Math.min(sayfa * sayfaBoyutu, total)} / {total} istek
              </p>
              <div className='flex items-center gap-1'>
                <button
                  onClick={() => setSayfa(1)}
                  disabled={sayfa === 1}
                  className='px-2.5 py-1.5 text-xs rounded-lg border border-gray-200
                             hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
                             transition-colors'>
                  İlk
                </button>
                <button
                  onClick={() => setSayfa(s => Math.max(1, s - 1))}
                  disabled={sayfa === 1}
                  className='p-1.5 rounded-lg border border-gray-200
                             hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
                             transition-colors'>
                  <ChevronLeft size={14} />
                </button>

                {Array.from({ length: toplamSayfa }, (_, i) => i + 1)
                  .filter(s => s === 1 || s === toplamSayfa || Math.abs(s - sayfa) <= 2)
                  .reduce((acc, s, i, arr) => {
                    if (i > 0 && s - arr[i - 1] > 1) acc.push('...');
                    acc.push(s);
                    return acc;
                  }, [])
                  .map((s, i) =>
                    s === '...' ? (
                      <span key={`dot-${i}`} className='px-1 text-slate-400 text-xs'>…</span>
                    ) : (
                      <button key={s} onClick={() => setSayfa(s)}
                        className={`w-8 h-8 text-xs rounded-lg border transition-colors
                          ${s === sayfa
                            ? 'bg-blue-500 text-white border-blue-500 font-semibold'
                            : 'border-gray-200 hover:bg-gray-50 text-slate-600'}`}>
                        {s}
                      </button>
                    )
                  )}

                <button
                  onClick={() => setSayfa(s => Math.min(toplamSayfa, s + 1))}
                  disabled={sayfa === toplamSayfa}
                  className='p-1.5 rounded-lg border border-gray-200
                             hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
                             transition-colors'>
                  <ChevronRight size={14} />
                </button>
                <button
                  onClick={() => setSayfa(toplamSayfa)}
                  disabled={sayfa === toplamSayfa}
                  className='px-2.5 py-1.5 text-xs rounded-lg border border-gray-200
                             hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed
                             transition-colors'>
                  Son
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Request Detay Drawer */}
      {secilenGroup && (
        <>
          <div
            className='fixed inset-0 bg-black/20 z-40'
            onClick={() => setSecilenGroup(null)}
          />
          <RequestTimeline
            group={secilenGroup}
            onClose={() => setSecilenGroup(null)}
          />
        </>
      )}
    </div>
  );
}
